import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Layers,
  Plus,
  Pencil,
  Trash2,
  Users,
  Calendar,
  Clock,
  ChevronRight,
  Search,
  X,
  FilterX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CardSkeleton } from "@/components/loading";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { PaginationControls } from "@/components/PaginationControls";
import { usePaginationState } from "@/hooks/use-pagination-state";
import { toast } from "sonner";
import Swal from "sweetalert2";
import {
  fetchBatchesPaginated,
  createBatch,
  updateBatch,
  deleteBatch,
  fetchTeams,
} from "@/lib/api/teams";
import { RequirePermission } from "@/components/auth/RequirePermission";
import { AccessDenied } from "@/components/auth/AccessDenied";

export const Route = createFileRoute("/_authenticated/admin/batches")({
  component: () => (
    <RequirePermission
      action="manage:teams"
      fallback={<AccessDenied message="Batch management is limited to System Admins." />}
    >
      <BatchesPage />
    </RequirePermission>
  ),
});

function BatchesPage() {
  const queryClient = useQueryClient();

  // Pagination & Search States
  // Search + pagination are server-side (via fetchBatchesPaginated). Start/end date filters are
  // NOT supported by the backend's /api/admin/batches search param, so they remain client-side
  // filters applied over the current page's items (see below).
  const { pageNumber, pageSize, search, setSearch, setPageNumber, setPageSize, reset } =
    usePaginationState({ initialPageSize: 9 });
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");

  const { data: batchesData, isLoading } = useQuery({
    queryKey: ["batches-paged", pageNumber, pageSize, search],
    queryFn: () => fetchBatchesPaginated({ pageNumber, pageSize, search: search || undefined }),
    placeholderData: (previousData) => previousData,
    retry: false,
  });

  const allBatches = batchesData?.items ?? [];

  // Fetch Groups to display count/names
  const { data: groups = [] } = useQuery({
    queryKey: ["groups"],
    queryFn: fetchTeams,
    retry: false,
  });

  // Dialog State
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    description: "",
    startDate: "",
    endDate: "",
  });

  // Filtered Batches Memo — date filters applied client-side over the current page only.
  const filteredBatches = useMemo(() => {
    return allBatches.filter((batch: any) => {
      // Start Date Filter (starts on or after target)
      if (startDateFilter) {
        if (!batch.startDate) return false;
        const batchStart = new Date(batch.startDate).setHours(0, 0, 0, 0);
        const filterStart = new Date(startDateFilter).setHours(0, 0, 0, 0);
        if (batchStart < filterStart) return false;
      }

      // End Date Filter (ends on or before target)
      if (endDateFilter) {
        if (!batch.endDate) return false;
        const batchEnd = new Date(batch.endDate).setHours(0, 0, 0, 0);
        const filterEnd = new Date(endDateFilter).setHours(0, 0, 0, 0);
        if (batchEnd > filterEnd) return false;
      }

      return true;
    });
  }, [allBatches, startDateFilter, endDateFilter]);

  // Mutations
  const createMut = useMutation({
    mutationFn: createBatch,
    onSuccess: () => {
      toast.success("Batch created successfully");
      queryClient.invalidateQueries({ queryKey: ["batches-paged"] });
      setIsCreateOpen(false);
    },
    onError: (err: any) => toast.error(err.message || "Failed to create batch"),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updateBatch(id, data),
    onSuccess: () => {
      toast.success("Batch updated successfully");
      queryClient.invalidateQueries({ queryKey: ["batches-paged"] });
      setIsEditOpen(false);
    },
    onError: (err: any) => toast.error(err.message || "Failed to update batch"),
  });

  const deleteMut = useMutation({
    mutationFn: deleteBatch,
    onSuccess: () => {
      toast.success("Batch deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["batches-paged"] });
    },
    onError: (err: any) => toast.error(err.message || "Failed to delete batch"),
  });

  // Handlers
  const handleCreateSubmit = () => {
    if (!formData.name) return toast.error("Name is required");
    createMut.mutate({
      name: formData.name,
      description: formData.description,
      startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
      endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
      isActive: true,
    });
  };

  const handleEditSubmit = () => {
    if (!formData.name) return toast.error("Name is required");
    updateMut.mutate({
      id: formData.id,
      data: {
        name: formData.name,
        description: formData.description,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
        isActive: true,
      },
    });
  };

  const openEdit = (batch: any) => {
    setFormData({
      id: batch.id,
      name: batch.name,
      description: batch.description || "",
      startDate: batch.startDate ? batch.startDate.split("T")[0] : "",
      endDate: batch.endDate ? batch.endDate.split("T")[0] : "",
    });
    setIsEditOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 border-b pb-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-primary/10 text-primary shadow-inner">
              <Layers className="h-7 w-7" />
            </div>
            Batches Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            Create and manage cohort batches, track duration milestones, and view assigned groups.
          </p>
        </div>
        <Button
          onClick={() => {
            setFormData({ id: "", name: "", description: "", startDate: "", endDate: "" });
            setIsCreateOpen(true);
          }}
          className="bg-primary hover:bg-primary/95 text-primary-foreground font-semibold gap-2 rounded-xl h-10 shadow-md cursor-pointer transition-all hover:shadow-lg"
        >
          <Plus className="h-4 w-4" />
          Create Batch
        </Button>
      </div>

      {/* Filters Panel */}
      <div className="bg-card border rounded-2xl p-5 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground/90">Filter Cohorts</h2>
          {(search || startDateFilter || endDateFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch("");
                setStartDateFilter("");
                setEndDateFilter("");
                reset();
              }}
              className="text-xs text-muted-foreground hover:text-destructive h-8 px-2.5 rounded-lg"
            >
              <FilterX className="h-3.5 w-3.5 mr-1" />
              Clear Filters
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Text search (server-side) */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Search className="h-3 w-3" /> Search Text
            </label>
            <div className="relative">
              <Input
                placeholder="Search name or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pr-9 h-10 rounded-xl bg-muted/20 border-muted/70 focus-visible:ring-primary focus-visible:bg-background transition-all text-sm w-full text-foreground"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Start Date (client-side over current page — backend search doesn't support it) */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Start Date (From)
            </label>
            <div className="relative">
              <Input
                type="date"
                value={startDateFilter}
                onChange={(e) => {
                  setStartDateFilter(e.target.value);
                  reset();
                }}
                className="pr-9 h-10 rounded-xl bg-muted/20 border-muted/70 focus-visible:ring-primary focus-visible:bg-background transition-all text-sm w-full text-foreground [color-scheme:light] dark:[color-scheme:dark]"
              />
              {startDateFilter && (
                <button
                  onClick={() => {
                    setStartDateFilter("");
                    reset();
                  }}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* End Date (client-side over current page — backend search doesn't support it) */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" /> End Date (To)
            </label>
            <div className="relative">
              <Input
                type="date"
                value={endDateFilter}
                onChange={(e) => {
                  setEndDateFilter(e.target.value);
                  reset();
                }}
                className="pr-9 h-10 rounded-xl bg-muted/20 border-muted/70 focus-visible:ring-primary focus-visible:bg-background transition-all text-sm w-full text-foreground [color-scheme:light] dark:[color-scheme:dark]"
              />
              {endDateFilter && (
                <button
                  onClick={() => {
                    setEndDateFilter("");
                    reset();
                  }}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Results summary */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
          <div>
            Showing <span className="font-semibold text-foreground">{filteredBatches.length}</span>{" "}
            of <span className="font-semibold text-foreground">{batchesData?.totalCount ?? 0}</span>{" "}
            total batches
            {(startDateFilter || endDateFilter) && (
              <span className="ml-1">(date filters apply to the current page only)</span>
            )}
          </div>
          {(startDateFilter || endDateFilter) && (
            <div className="flex items-center gap-1.5 bg-primary/5 text-primary px-2.5 py-1 rounded-md border border-primary/10">
              <Calendar className="h-3 w-3" />
              <span>Date filter active</span>
            </div>
          )}
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <CardSkeleton count={6} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" />
      ) : filteredBatches.length === 0 ? (
        <div className="p-16 text-center border-dashed rounded-2xl border bg-card">
          <Layers className="h-10 w-10 text-muted-foreground/35 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm font-medium">
            No batches match the filtering criteria.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBatches.map((batch: any, index: number) => (
            <div
              key={batch.id}
              className="group relative p-6 bg-card border rounded-2xl shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-300 flex flex-col justify-between overflow-hidden animate-in fade-in slide-in-from-bottom-4"
              style={{ animationDelay: `${index * 45}ms`, animationFillMode: "both" }}
            >
              {/* Decorative top gradient utilizing site colors (Navy to Gold Accent) */}
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-primary via-primary/80 to-accent opacity-85" />

              <div>
                <div className="flex justify-between items-start gap-2">
                  <h3
                    className="font-bold text-lg text-foreground tracking-tight group-hover:text-primary transition-colors truncate"
                    title={batch.name}
                  >
                    {batch.name}
                  </h3>
                  <div className="flex items-center gap-1 bg-success-muted text-success border border-success/20 px-2.5 py-0.5 rounded-full text-[10px] font-bold shrink-0">
                    <span className="relative flex h-2 w-2 mr-1">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
                    </span>
                    Active
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mt-2 line-clamp-2 leading-relaxed min-h-[40px]">
                  {batch.description || "No description provided for this batch."}
                </p>

                {/* Date Milestones display block */}
                <div className="flex flex-wrap gap-4 mt-5 text-xs text-muted-foreground font-medium bg-muted/20 p-3 rounded-2xl border border-muted/40">
                  {batch.startDate ? (
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-4 w-4 text-primary" />
                      <span>
                        {new Date(batch.startDate).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-muted-foreground/60">
                      <Calendar className="h-4 w-4" />
                      <span>No start date</span>
                    </div>
                  )}
                  {batch.endDate ? (
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-4 w-4 text-accent" />
                      <span>
                        {new Date(batch.endDate).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-muted-foreground/60">
                      <Clock className="h-4 w-4" />
                      <span>No end date</span>
                    </div>
                  )}
                </div>

                {/* Groups Display */}
                <div className="mt-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-xs font-bold text-foreground">
                      <Users className="h-4 w-4 text-primary" />
                      Assigned Groups ({groups.filter((g: any) => g.batch_id === batch.id).length})
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {groups
                      .filter((g: any) => g.batch_id === batch.id)
                      .map((g: any) => (
                        <Link
                          key={g.id}
                          to="/admin/groups/$groupId"
                          params={{ groupId: g.id }}
                          className="group/link flex items-center gap-1 text-xs bg-background text-foreground px-3 py-1.5 rounded-xl font-semibold border border-border shadow-sm hover:border-primary/40 hover:text-primary transition-all"
                        >
                          {g.name}
                          <ChevronRight className="h-3 w-3 opacity-50 group-hover/link:opacity-100 group-hover/link:translate-x-0.5 transition-all text-primary" />
                        </Link>
                      ))}
                    {groups.filter((g: any) => g.batch_id === batch.id).length === 0 && (
                      <div className="w-full text-center py-3 px-4 border border-dashed rounded-xl bg-muted/10 text-xs text-muted-foreground italic">
                        No groups have been assigned to this batch.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Card Footer Actions */}
              <div className="flex items-center justify-end border-t border-muted/50 mt-6 pt-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => openEdit(batch)}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground cursor-pointer rounded-xl hover:bg-muted/70"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    Swal.fire({
                      title: "Are you sure?",
                      text: `You won't be able to revert this! Delete batch "${batch.name}"?`,
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonColor: "#163569",
                      cancelButtonColor: "#ef4444",
                      confirmButtonText: "Yes, delete it!",
                      customClass: {
                        popup: "rounded-2xl",
                        confirmButton: "rounded-xl font-semibold px-4 py-2 text-sm",
                        cancelButton: "rounded-xl font-semibold px-4 py-2 text-sm",
                      },
                    }).then((result) => {
                      if (result.isConfirmed) {
                        deleteMut.mutate(batch.id);
                      }
                    });
                  }}
                  className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 cursor-pointer rounded-xl"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls (server-side) */}
      <PaginationControls
        pageNumber={pageNumber}
        pageSize={pageSize}
        totalPages={batchesData?.totalPages ?? 1}
        totalCount={batchesData?.totalCount ?? 0}
        hasNextPage={batchesData?.hasNextPage ?? false}
        hasPreviousPage={batchesData?.hasPreviousPage ?? false}
        onPageChange={setPageNumber}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
      />

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-md bg-card border rounded-2xl shadow-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Create Batch</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">Batch Name</label>
              <Input
                placeholder="e.g. Cohort 2026-Q3"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-xl focus-visible:ring-primary text-foreground"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">Description</label>
              <Textarea
                placeholder="Optional details about this batch..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-xl focus-visible:ring-primary text-foreground min-h-[80px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Start Date</label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="rounded-xl focus-visible:ring-primary text-foreground [color-scheme:light] dark:[color-scheme:dark]"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">End Date</label>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="rounded-xl focus-visible:ring-primary text-foreground [color-scheme:light] dark:[color-scheme:dark]"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button variant="outline" onClick={() => setIsCreateOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={handleCreateSubmit}
              disabled={createMut.isPending}
              className="rounded-xl bg-primary hover:bg-primary/95 text-primary-foreground font-semibold cursor-pointer"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-md bg-card border rounded-2xl shadow-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Edit Batch</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">Batch Name</label>
              <Input
                placeholder="Batch Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-xl focus-visible:ring-primary text-foreground"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">Description</label>
              <Textarea
                placeholder="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-xl focus-visible:ring-primary text-foreground min-h-[80px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Start Date</label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="rounded-xl focus-visible:ring-primary text-foreground [color-scheme:light] dark:[color-scheme:dark]"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">End Date</label>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="rounded-xl focus-visible:ring-primary text-foreground [color-scheme:light] dark:[color-scheme:dark]"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button variant="outline" onClick={() => setIsEditOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={handleEditSubmit}
              disabled={updateMut.isPending}
              className="rounded-xl bg-primary hover:bg-primary/95 text-primary-foreground font-semibold cursor-pointer"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
